import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "./authService";

// API functions for vendor services
export const vendorServiceApi = {
  // Get all services for a vendor
  getVendorServices: async (vendorId, params = {}) => {
    const response = await api.get(`/vendors/${vendorId}/services`, { params });
    return response.data;
  },

  // Get current vendor's services
  getMyServices: async (params = {}) => {
    const response = await api.get('/vendor/services', { params });
    return response.data;
  },

  // Get service by ID
  getById: async (serviceId) => {
    const response = await api.get(`/vendor/services/${serviceId}`);
    return response.data;
  },

  // Create new service
  create: async (data) => {
    const response = await api.post('/vendor/services', data);
    return response.data;
  },

  // Update service
  update: async ({ id, data }) => {
    const response = await api.put(`/vendor/services/${id}`, data);
    return response.data;
  },

  // Delete service
  delete: async (id) => {
    const response = await api.delete(`/vendor/services/${id}`);
    return response.data;
  },

  // Toggle service status (active/inactive)
  toggleStatus: async (id) => {
    const response = await api.put(`/vendor/services/${id}/toggle-status`);
    return response.data;
  },

  // Upload service images
  uploadImages: async (serviceId, files) => {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`images[${index}]`, file);
    });
    
    const response = await api.post(`/vendor/services/${serviceId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete service image
  deleteImage: async (serviceId, imageId) => {
    const response = await api.delete(`/vendor/services/${serviceId}/images/${imageId}`);
    return response.data;
  },

  // Get service analytics
  getAnalytics: async (serviceId, params = {}) => {
    const response = await api.get(`/vendor/services/${serviceId}/analytics`, { params });
    return response.data;
  }
};

// React Query hooks
export const useVendorServices = (vendorId, params = {}) => {
  return useQuery({
    queryKey: ['vendor-services', vendorId, params],
    queryFn: () => vendorServiceApi.getVendorServices(vendorId, params),
    enabled: !!vendorId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useMyServices = (params = {}) => {
  return useQuery({
    queryKey: ['my-services', params],
    queryFn: () => vendorServiceApi.getMyServices(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useVendorService = (serviceId) => {
  return useQuery({
    queryKey: ['vendor-service', serviceId],
    queryFn: () => vendorServiceApi.getById(serviceId),
    enabled: !!serviceId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useCreateVendorService = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: vendorServiceApi.create,
    onSuccess: (data) => {
      // Invalidate and refetch services
      queryClient.invalidateQueries({ queryKey: ['my-services'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-services'] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useUpdateVendorService = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: vendorServiceApi.update,
    onSuccess: (data, variables) => {
      // Invalidate and refetch services
      queryClient.invalidateQueries({ queryKey: ['my-services'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-services'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-service', variables.id] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useDeleteVendorService = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: vendorServiceApi.delete,
    onSuccess: (data, serviceId) => {
      // Invalidate and refetch services
      queryClient.invalidateQueries({ queryKey: ['my-services'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-services'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-service', serviceId] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useToggleServiceStatus = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: vendorServiceApi.toggleStatus,
    onSuccess: (data, serviceId) => {
      // Invalidate and refetch services
      queryClient.invalidateQueries({ queryKey: ['my-services'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-services'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-service', serviceId] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useUploadServiceImages = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, files }) => vendorServiceApi.uploadImages(serviceId, files),
    onSuccess: (data, variables) => {
      // Invalidate and refetch service data
      queryClient.invalidateQueries({ queryKey: ['vendor-service', variables.serviceId] });
      queryClient.invalidateQueries({ queryKey: ['my-services'] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useDeleteServiceImage = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, imageId }) => vendorServiceApi.deleteImage(serviceId, imageId),
    onSuccess: (data, variables) => {
      // Invalidate and refetch service data
      queryClient.invalidateQueries({ queryKey: ['vendor-service', variables.serviceId] });
      queryClient.invalidateQueries({ queryKey: ['my-services'] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useServiceAnalytics = (serviceId, params = {}) => {
  return useQuery({
    queryKey: ['service-analytics', serviceId, params],
    queryFn: () => vendorServiceApi.getAnalytics(serviceId, params),
    enabled: !!serviceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export default vendorServiceApi;
